import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  useToast,
} from '@chakra-ui/react';
import IconDotGrid1x3Vertical from 'central-icons/IconDotGrid1x3Vertical';
import mixpanel from 'mixpanel-browser';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { BuildADashTrackingEvents, PagePaths } from '@revelio/core';

import { showAlertDialog } from '../../../../../shared/components/alert-dialog';
import type { ReportPageConfig } from '../../../../CustomDash/components/PageConfigProvider/types';
import { usePageManager } from '../../../../CustomDash/components/PageManagerProvider/PageManagerProvider';
import { ParameterEntitiesEditor } from '../../../../CustomDash/components/Reports/Parameters/ParameterEntitiesEditor';
import { parametersFromPageConfig } from '../../../../CustomDash/components/Reports/Parameters/parameters';
import { useAddParameterFilterGroup } from '../../../../CustomDash/components/Reports/Parameters/useAddParameterFilterGroup';
import { DeliverableStepContainer } from '../../../../deliverables/deliverable-step-container';
import { useGlobalAssistantPromotedReportPrefill } from '../../../../global-ai-assistant/use-global-ai-assistant-promoted-report-prefill';
import {
  getReportBuilderPath,
  ReportBuilderStep,
} from '../../../utils/report-builder-paths';
import { CustomReportBuilderContext } from '../../custom-report-builder-provider';
import { ReportBuilderDeliverablePaths } from '../../report-builder-page';

const CustomReportsEntityConfigurationBody = ({
  selectedReportTemplate,
  canContinue,
  setCanContinue,
  onSubmit,
  startOver,
  deletePage,
  clonePageDraft,
  editPage,
}: {
  selectedReportTemplate: ReportPageConfig;
  canContinue: boolean;
  setCanContinue: (value: boolean) => void;
  onSubmit: () => void;
  startOver: () => void;
  deletePage: ReturnType<typeof usePageManager>['deletePage'];
  clonePageDraft: ReturnType<typeof usePageManager>['clonePageDraft'];
  editPage: ReturnType<typeof usePageManager>['editPage'];
}) => {
  const parameters = parametersFromPageConfig(selectedReportTemplate);
  useAddParameterFilterGroup({ parameters });
  useGlobalAssistantPromotedReportPrefill({ parameters });

  return (
    <DeliverableStepContainer
      id="report_builder_entity"
      title={`Select ${selectedReportTemplate.name ?? 'Report'} Parameters`}
      titleElement={
        <div>
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<IconDotGrid1x3Vertical size={12} />}
              size="sm"
              aria-label="Actions"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            <Portal>
              <MenuList>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    editPage(selectedReportTemplate.id);
                    mixpanel.track(BuildADashTrackingEvents.EDIT_PAGE, {
                      page_id: selectedReportTemplate.id,
                    });
                  }}
                >
                  Edit Page
                </MenuItem>
                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    clonePageDraft(selectedReportTemplate.id);
                    mixpanel.track(BuildADashTrackingEvents.CLONE_PAGE, {
                      page_id: selectedReportTemplate.id,
                    });
                  }}
                >
                  Make a Copy
                </MenuItem>

                <MenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    showAlertDialog({
                      alertType: 'confirm',
                      isOpen: true,
                      headerText:
                        'Are you sure you want to delete this report?',
                      bodyContent: (
                        <span>
                          This action is permanent and cannot be undone.
                        </span>
                      ),
                      footerAcceptIsDestructive: true,
                      footerAcceptText: 'Yes, Delete',
                      onAccept: async () => {
                        deletePage(selectedReportTemplate.id);
                        mixpanel.track(BuildADashTrackingEvents.DELETE_PAGE, {
                          page_id: selectedReportTemplate.id,
                        });
                        startOver();
                      },
                    });
                  }}
                >
                  Delete
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </div>
      }
      previousStepPath={getReportBuilderPath(ReportBuilderStep.REPORT_TYPE)}
      onSubmit={onSubmit}
      isSubmitDisabled={!canContinue}
      submitButtonContent="Continue"
      startOverPath={getReportBuilderPath(ReportBuilderStep.REPORT_TYPE)}
      isReportStep={true}
    >
      <ParameterEntitiesEditor
        parameters={parameters}
        onCanCreateReport={setCanContinue}
      />
    </DeliverableStepContainer>
  );
};

export const CustomReportsEntityConfigurations = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { selectedReportTemplate, setReportTemplate } = useContext(
    CustomReportBuilderContext
  );
  const [canContinue, setCanContinue] = useState<boolean>(false);
  const { deletePage, clonePageDraft, editPage } = usePageManager();

  const onSubmit = () => {
    if (!canContinue) {
      toast({
        title: 'Please select required parameters',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    navigate(
      `/${PagePaths.REPORT_BUILDER}/${ReportBuilderDeliverablePaths.CONFIRMATION}`
    );
  };

  useEffect(() => {
    if (!selectedReportTemplate) {
      navigate(
        `/${PagePaths.REPORT_BUILDER}/${ReportBuilderDeliverablePaths.REPORT_TYPE}`
      );
    }
  }, [selectedReportTemplate, navigate]);

  const startOver = () => {
    navigate(getReportBuilderPath(ReportBuilderStep.REPORT_TYPE));
    setReportTemplate(null);
  };

  if (!selectedReportTemplate) {
    return null;
  }

  return (
    <CustomReportsEntityConfigurationBody
      selectedReportTemplate={selectedReportTemplate}
      canContinue={canContinue}
      setCanContinue={setCanContinue}
      onSubmit={onSubmit}
      startOver={startOver}
      deletePage={deletePage}
      clonePageDraft={clonePageDraft}
      editPage={editPage}
    />
  );
};
